/**
 * A Resource Plan's NOS Deal Stage and Chance of Close (spec Epic 36 / RP-36.1).
 *
 * Pure: everything here is a function of rows already read, so the rules are tested
 * without a database.
 *
 * NOS CRM is the source of truth for the Deal's stage and percent-to-close. Staff keeps
 * its own per-plan **Chance of Close**: it follows NOS's percent until someone picks a
 * value of their own (with a who-said-it comment), and goes back to following NOS when
 * someone picks "Use NOS". It replaces the spec's hand-typed "Confidence %".
 *
 * Won and Lost are NOS's call only — the option list stops below 100, and nothing here
 * moves a plan's status. The "disagreement" is a prompt for a person.
 */
import { AFTER_WON_STATUSES, effectivePlanStatus } from "@/lib/resource-plan-lifecycle";

/** The default option list for Settings → Planning ("Chance of Close options"). */
export const DEFAULT_CHANCE_OF_CLOSE_OPTIONS = "10,25,35,50,75,90";

/** The shortest who-said-it comment accepted — also the DB CHECK. */
export const CHANCE_OF_CLOSE_COMMENT_MIN = 10;

/**
 * Parse the configured option list: whole percents from 0 to 99, comma-separated,
 * strictly ascending, at least one and at most ten. Returns null when the text is not a
 * valid list — the Planning Constant's schema uses this to refuse a bad write.
 */
export function parseChanceOfCloseOptions(raw: string): number[] | null {
  const parts = raw.split(",").map((p) => p.trim());
  if (parts.length === 0 || parts.length > 10) return null;
  const values: number[] = [];
  for (const part of parts) {
    if (!/^\d{1,2}$/.test(part)) return null;
    const value = Number(part);
    const previous = values[values.length - 1];
    if (previous !== undefined && value <= previous) return null;
    values.push(value);
  }
  return values;
}

export type ChanceOfCloseSource = "NOS" | "Staff";

export interface ChanceOfCloseChange {
  /** Staff's figure, or null for "Use NOS". */
  percent: number | null;
}

export interface ResolvedChanceOfClose {
  /** The plan's Chance of Close, 0–100; null when neither Staff nor NOS has a figure. */
  percent: number | null;
  /** Where `percent` came from; null when there is no figure at all. */
  source: ChanceOfCloseSource | null;
}

/**
 * The plan's current Chance of Close: the newest change's figure if it set one,
 * otherwise NOS's percent-to-close. `latest` is the newest history row, or null when
 * nobody has ever changed it.
 */
export function resolveChanceOfClose(
  latest: ChanceOfCloseChange | null,
  nosPercent: number | null,
): ResolvedChanceOfClose {
  if (latest !== null && latest.percent !== null) {
    return { percent: latest.percent, source: "Staff" };
  }
  if (nosPercent !== null) return { percent: nosPercent, source: "NOS" };
  return { percent: null, source: null };
}

/**
 * Where the plan and its NOS Deal disagree — a prompt for a person, never an automatic
 * status move (decision D3):
 *   - "won_but_deal_open": the plan is Won or later, but the Deal is still open in NOS's
 *     pipeline.
 *   - "deal_left_pipeline": the plan is still Draft or Proposed, but the Deal has left
 *     NOS's pipeline (closed — won or lost; the feed does not say which).
 * A Locked plan is judged by the status it was locked from.
 */
export type PlanDealDrift = "won_but_deal_open" | "deal_left_pipeline";

export function planDealDrift(
  plan: { status: string; lockedFromStatus: string | null },
  deal: { openInNos: boolean } | null,
): PlanDealDrift | null {
  if (deal === null) return null;
  const status = effectivePlanStatus(plan);
  const afterWon = AFTER_WON_STATUSES.some((s) => s === status);
  if (afterWon && deal.openInNos) return "won_but_deal_open";
  if ((status === "Draft" || status === "Proposed") && !deal.openInNos) {
    return "deal_left_pipeline";
  }
  return null;
}
